import { Loader2, Search } from "lucide-react";
import { useEffect, useRef } from "react";
import type { Team, UserSearchResult } from "@/types";
import { TeamIcon, UserIcon } from "./MemberAvatars";

/** Search box + dropdown for adding people/teams as members. */
export function MemberSearchBox({
  query,
  setQuery,
  showResults,
  setShowResults,
  isSearching,
  teamResults,
  userResults,
  busyId,
  onAddTeam,
  onAddUser,
}: Readonly<{
  query: string;
  setQuery: (v: string) => void;
  showResults: boolean;
  setShowResults: (v: boolean) => void;
  isSearching: boolean;
  teamResults: Team[];
  userResults: UserSearchResult[];
  busyId: string | null;
  onAddTeam: (t: Team) => void;
  onAddUser: (u: UserSearchResult) => void;
}>) {
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showResults) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setShowResults(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showResults, setShowResults]);

  return (
    <div className="relative" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowResults(true);
        }}
        onFocus={() => setShowResults(true)}
        placeholder="Add people or teams..."
        className="w-full pl-9 pr-9 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary text-sm"
      />
      {isSearching && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary animate-spin" />
      )}
      {showResults && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-surface-primary border border-border-secondary rounded-lg shadow-xl">
          {teamResults.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-quaternary">
                Teams
              </div>
              {teamResults.map((team) => (
                <button
                  key={`team-${team.uuid}`}
                  type="button"
                  onClick={() => onAddTeam(team)}
                  disabled={busyId === team.uuid}
                  className="flex items-center gap-3 w-full p-2.5 hover:bg-interactive-hover transition text-left disabled:opacity-50"
                >
                  <TeamIcon />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-content-primary font-medium truncate">{team.name}</div>
                    <div className="text-xs text-content-quaternary">Team</div>
                  </div>
                </button>
              ))}
            </>
          )}
          {userResults.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-quaternary">
                People
              </div>
              {userResults.map((user) => (
                <button
                  key={`user-${user.uuid}`}
                  type="button"
                  onClick={() => onAddUser(user)}
                  disabled={busyId === user.uuid}
                  className="flex items-center gap-3 w-full p-2.5 hover:bg-interactive-hover transition text-left disabled:opacity-50"
                >
                  <UserIcon name={user.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-content-primary font-medium truncate">{user.name}</div>
                    <div className="text-xs text-content-quaternary truncate">{user.email}</div>
                  </div>
                </button>
              ))}
            </>
          )}
          {query.trim().length < 1 && (
            <div className="px-3 py-2 text-xs text-content-quaternary border-t border-border-secondary/60">
              Type a name or email to find people.
            </div>
          )}
          {query.trim().length >= 1 && !isSearching && teamResults.length === 0 && userResults.length === 0 && (
            <div className="px-3 py-2.5 text-sm text-content-quaternary">No people or teams found</div>
          )}
        </div>
      )}
    </div>
  );
}
